// Copyright ©️ 2026 eVolve MEP, LLC

import { type UserId, type AssignableRole, type RoleEntry, useEvolvePost } from '@evolvemep/api-ts';
import { Table } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { isNotNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';
import type { HasProductsAvailable } from 'modules/Admin/CompanyMembers/types';

type Props = {
  userIds: UserId[];
  opened: boolean;
  assignableRoles: AssignableRole[];
  hasProducts: HasProductsAvailable;
  onClose: () => void;
  refresh: () => void;
};

type FormValues = {
  mechanicalRole: RoleEntry['roleId'];
  electricalRole: RoleEntry['roleId'];
  shopRole: RoleEntry['roleId'];
  fabricationRole: RoleEntry['roleId'];
};

const AssignRolesForm = ({ userIds, refresh, hasProducts, assignableRoles }: Props) => {
  const { apiCall: assignRoles, loading } = useEvolvePost('admin/role/assign');

  const form = useForm<FormValues>({
    initialValues: {
      mechanicalRole: null,
      electricalRole: null,
      shopRole: null,
      fabricationRole: null,
    },
  });

  const onSubmit = async () => {
    const roleIds = strongEntries(form.values)
      .filter(([key]) => form.isDirty(key))
      .map(([_, roleId]) => roleId)
      .filter(isNotNil);
    await assignRoles({
      userRoleAssignments: userIds.flatMap((userId) =>
        roleIds.map((roleId) => ({
          userId,
          roleId,
        })),
      ),
    }).then(() => {
      notifications.show({
        title: 'Successfully assigned',
        message: `Assigned role${roleIds.length !== 0 ? 's' : ''} to ${userIds.length} member${userIds.length === 1 ? '' : 's'}`,
        color: 'green',
      });
      refresh();
    });
  };

  useFormModal(form, onSubmit);

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Product</Table.Th>
          <Table.Th>New Role</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {hasProducts.revitRoles && (
          <>
            {hasProducts.electrical && (
              <Table.Tr>
                <Table.Td>Electrical</Table.Td>
                <Table.Td>
                  <WrappedSelect
                    {...form.getInputProps('electricalRole')}
                    placeholder="No change"
                    clearable
                    onClear={() => {
                      form.setDirty({ electricalRole: false });
                    }}
                    disabled={loading}
                    data={assignableRoles
                      .filter((r) => r.scope === 'EE')
                      .map((r) => ({ label: r.displayName, value: r.roleId }))}
                  />
                </Table.Td>
              </Table.Tr>
            )}
            {hasProducts.mechanical && (
              <Table.Tr>
                <Table.Td>Mechanical</Table.Td>
                <Table.Td>
                  <WrappedSelect
                    {...form.getInputProps('mechanicalRole')}
                    placeholder="No change"
                    clearable
                    onClear={() => {
                      form.setDirty({ mechanicalRole: false });
                    }}
                    disabled={loading}
                    data={assignableRoles
                      .filter((r) => r.scope === 'EM')
                      .map((r) => ({ label: r.displayName, value: r.roleId }))}
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </>
        )}
        {hasProducts.shop && (
          <Table.Tr>
            <Table.Td>Shop</Table.Td>
            <Table.Td>
              <WrappedSelect
                {...form.getInputProps('shopRole')}
                placeholder="No change"
                clearable
                onClear={() => {
                  form.setDirty({ shopRole: false });
                }}
                disabled={loading}
                data={assignableRoles
                  .filter((r) => r.scope === 'Shop')
                  .map((r) => ({ label: r.displayName, value: r.roleId }))}
              />
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  );
};

export const AssignRolesModal = (props: Props) => {
  const { userIds, opened, onClose } = props;

  return (
    <FormModal
      title="Assign roles"
      subtitle={`${userIds.length} member${userIds.length === 1 ? '' : 's'} selected`}
      opened={opened}
      onClose={onClose}
      confirmationText="Assign"
      type="submit"
    >
      <AssignRolesForm {...props} />
    </FormModal>
  );
};
